// Shared invite modal for circles + networks. Mounted from CircleDetailView
// and NetworkDetailView. Sections (in order):
//   1 — Invite member (typeahead, in-app notifications on accept)
//   2 — Invite as leader (single batch-level checkbox)
//   3 — Invite by email (comma-separated chips, per-email status probe)
//       + Personal note (included in the email)
//
// Invite-by-link is gone — the server only supports per-recipient invites.
// All recipients in one batch share the same `roleOnAccept`, controlled by
// the "Invite as leader" checkbox above the email input.
//
// Group kind drives only which API routes the fetchers + mutations hit
// (`/api/v1/circles/{id}/*` vs `/api/v1/networks/{id}/*`); the underlying
// surfaces are identical (lifted into shared services on the server in
// `service/group-invites.ts`).
//
// After Send, the modal switches to a result step that surfaces every
// outcome — created invites, on-platform users that got auto-added, and
// recipients the server skipped (duplicate/already_member/invalid). The old
// modal closed on success and dropped `skipped` on the floor, which
// translated to silent "invite never arrived" support tickets.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
// Single-feature glyph (chip status dot for "invite already pending"). Per
// `@leanin/ui` icon convention, one-off glyphs live with the feature that
// uses them rather than expanding the curated `I` map.
import {
  Clock as ClockGlyph,
  ChevronUp as ArrowUpGlyph,
} from 'lucide-react';
import {
  AlertDialog,
  Avatar,
  Body,
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  DialogSection,
  Field,
  Heading,
  I,
  Icon,
  Input,
  MultiCombobox,
  Notice,
  PersonRow,
  Pill,
  Row,
  Stack,
  Textarea,
  TONE,
  Tooltip,
  type ComboboxOption,
} from '@leanin/ui';
import type { components } from '@leanin/api-client';
import {
  fetchGroupInviteNote,
  fetchInvitableMembers,
  fetchInviteEmailStatus,
  type GroupKind,
  type InviteEmailStatus,
  groupKeys,
} from '../../lib/queries/groups';
import {
  useCreateGroupInvites,
  useSetGroupInviteNote,
} from '../../lib/mutations/groups';
import { useResetOnClose } from '../circles/hooks/use-reset-on-close';

const EMAIL_RE = /^.+@.+\..+$/;
const SEARCH_DEBOUNCE_MS = 200;

type BulkInviteResult = components['schemas']['BulkInviteCreatedResponse'];

export type InviteMemberModalProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly kind: GroupKind;
  readonly groupId: string;
  readonly groupName: string;
};

type PickedMember = {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
};

type TypeaheadHit = {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly photoUrl: string | null;
  readonly jobTitle: string | null;
  readonly company: string | null;
};

// Per-chip status, projected from the server's `InviteEmailStatus` union.
// `pending` is the in-flight state; `error` is the soft-fail fallback so the
// chip stays sendable (the create endpoint will surface a hard error on
// submit if it can't proceed).
type EmailMatchState =
  | 'pending'
  | { readonly kind: 'invalid_email' }
  | { readonly kind: 'off_platform' }
  | {
      readonly kind: 'on_platform_invitable';
      readonly name: string;
      readonly sublabel: string;
      readonly photoUrl: string | null;
    }
  | {
      readonly kind: 'already_member';
      readonly name: string;
      readonly sublabel: string;
      readonly role: 'member' | 'leader';
      readonly photoUrl: string | null;
    }
  | {
      readonly kind: 'pending_invite';
      readonly inviterName: string;
      readonly sentAtIso: string;
    }
  | { readonly kind: 'error' };

// A chip is blocking (excluded from the submit count) when its server-resolved
// state means the bulk-create endpoint will skip it. `already_member` is the
// nuanced one: a member-role recipient invited as leader is NOT blocking —
// the server promotes them in place. Members invited as members (or leaders
// invited as anything) ARE blocking.
function isBlockingState(
  state: EmailMatchState | undefined,
  roleOnAccept: 'member' | 'leader',
): boolean {
  if (state === undefined || state === 'pending') return false;
  if (state.kind === 'invalid_email' || state.kind === 'pending_invite') {
    return true;
  }
  if (state.kind === 'already_member') {
    // Promote case is NOT blocking — the server handles it in-tx.
    return !(state.role === 'member' && roleOnAccept === 'leader');
  }
  return false;
}

function projectStatus(s: InviteEmailStatus): EmailMatchState {
  switch (s.status) {
    case 'invalid_email':
      return { kind: 'invalid_email' };
    case 'off_platform':
      return { kind: 'off_platform' };
    case 'on_platform_invitable': {
      const name = fullName(s.member.firstName, s.member.lastName);
      const sublabel = memberSublabel(s.member.jobTitle, s.member.company);
      return {
        kind: 'on_platform_invitable',
        name,
        sublabel,
        photoUrl: s.member.photoUrl,
      };
    }
    case 'already_member': {
      const name = fullName(s.member.firstName, s.member.lastName);
      const sublabel = memberSublabel(s.member.jobTitle, s.member.company);
      return {
        kind: 'already_member',
        name,
        sublabel,
        role: s.role,
        photoUrl: s.member.photoUrl,
      };
    }
    case 'pending_invite': {
      // Empty inviterName signals a system-issued invite (no human
      // inviter); the consumer at line 939 already branches on
      // `inviterName.length > 0` to fall back to a generic message.
      const inviterName = s.invite.invitedBy
        ? fullName(s.invite.invitedBy.firstName, s.invite.invitedBy.lastName)
        : '';
      return {
        kind: 'pending_invite',
        inviterName,
        sentAtIso: s.invite.createdAt,
      };
    }
  }
}

function fullName(first: string, last: string): string {
  return [first, last].filter((p) => p.length > 0).join(' ').trim();
}

function memberSublabel(jobTitle: string | null, company: string | null): string {
  return [jobTitle, company].filter((p): p is string => !!p && p.length > 0).join(' · ');
}

function kindLabel(kind: GroupKind): 'Circle' | 'Network' {
  return kind === 'circle' ? 'Circle' : 'Network';
}

// "5 days ago" / "today" style relative formatter for pending-invite hints.
// Short and approximate is fine here — exact timestamps live in the leader's
// pending-invites admin list, not on this chip.
function relativeDays(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const days = Math.max(0, Math.round((Date.now() - then) / 86_400_000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  return `${Math.round(days / 30)} mo ago`;
}

export function InviteMemberModal({
  open,
  onOpenChange,
  kind,
  groupId,
  groupName,
}: InviteMemberModalProps) {
  const sendInvitesMutation = useCreateGroupInvites();

  // Two-step modal: compose -> result. The result step is held in component
  // state so the user can read the per-recipient breakdown before either
  // closing or going back to compose another batch.
  const [step, setStep] = useState<'compose' | 'result'>('compose');
  const [result, setResult] = useState<BulkInviteResult | null>(null);

  const [picked, setPicked] = useState<readonly PickedMember[]>([]);
  const [emails, setEmails] = useState<readonly string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailMatches, setEmailMatches] =
    useState<Record<string, EmailMatchState>>({});
  // Unified per-batch role. Applies to both picked-member invites and typed
  // emails — the server's `createGroupInvites` POST takes one `roleOnAccept`
  // for the whole batch.
  const [roleOnAccept, setRoleOnAccept] = useState<'member' | 'leader'>('member');

  // Personal note is stored server-side per (viewer, group) via
  // GET/PUT /{kind}/{id}/invite-prefs. Local textarea state holds the
  // unsaved-draft value; the user commits via the "Save as default" button.
  const setInviteNote = useSetGroupInviteNote();
  const inviteNoteKey =
    kind === 'circle'
      ? groupKeys.circle.inviteNote(groupId)
      : groupKeys.network.inviteNote(groupId);
  const inviteNoteQuery = useQuery({
    queryKey: inviteNoteKey,
    queryFn: () => fetchGroupInviteNote({ kind, groupId }),
    enabled: open,
  });
  const savedNote = inviteNoteQuery.data ?? '';
  const [note, setNote] = useState('');
  useEffect(() => {
    setNote(savedNote);
  }, [savedNote]);
  const noteDirty = note !== savedNote;
  const [noteSaveError, setNoteSaveError] = useState<string | null>(null);

  // One-time migration from the previous localStorage scheme. If a value
  // exists locally and the server has none, push it once then drop the
  // localStorage key. Safe to delete after a few releases.
  const noteStorageKey = `invite-note:${kind}:${groupId}`;
  useEffect(() => {
    if (!open) return;
    if (inviteNoteQuery.data === undefined) return;
    if (typeof window === 'undefined') return;
    if (inviteNoteQuery.data.length > 0) return;
    let local: string | null = null;
    try {
      local = window.localStorage.getItem(noteStorageKey);
    } catch {
      return;
    }
    if (local === null || local.length === 0) return;
    setInviteNote.mutate(
      { kind, groupId, note: local },
      {
        onSuccess: () => {
          try {
            window.localStorage.removeItem(noteStorageKey);
          } catch {
            // best-effort; storage may be disabled
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inviteNoteQuery.data, noteStorageKey, kind, groupId]);

  const [noteJustSaved, setNoteJustSaved] = useState(false);
  const noteSavedFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (noteSavedFlashRef.current !== null) clearTimeout(noteSavedFlashRef.current);
  }, []);

  const onSaveNoteAsDefault = () => {
    setNoteSaveError(null);
    setInviteNote.mutate(
      { kind, groupId, note },
      {
        onSuccess: () => {
          setNoteJustSaved(true);
          if (noteSavedFlashRef.current !== null) {
            clearTimeout(noteSavedFlashRef.current);
          }
          noteSavedFlashRef.current = setTimeout(
            () => setNoteJustSaved(false),
            2000,
          );
        },
        onError: (err) =>
          setNoteSaveError(
            err instanceof Error ? err.message : 'Could not save note.',
          ),
      },
    );
  };

  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<readonly TypeaheadHit[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const isSending = sendInvitesMutation.isPending;

  useResetOnClose(open, () => {
    setStep('compose');
    setResult(null);
    setPicked([]);
    setEmails([]);
    setEmailDraft('');
    setEmailError(null);
    setEmailMatches({});
    setRoleOnAccept('member');
    // `note` deliberately preserved — see noteStorageKey above.
    setSearch('');
    setHits([]);
    setError(null);
    setDiscardOpen(false);
  });

  // Debounced typeahead.
  useEffect(() => {
    if (!open) return;
    const q = search.trim();
    if (q.length === 0) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      void fetchInvitableMembers({ kind, groupId, q, limit: 8 }).then((rows) => {
        if (cancelled) return;
        setHits(rows);
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, search, kind, groupId]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.userId)), [picked]);
  const visibleHits = useMemo(
    () => hits.filter((h) => !pickedIds.has(h.userId)),
    [hits, pickedIds],
  );

  const pickerOptions = useMemo<ReadonlyArray<ComboboxOption<string>>>(
    () =>
      visibleHits.map((h) => ({
        value: h.userId,
        label: fullName(h.firstName, h.lastName),
      })),
    [visibleHits],
  );
  const pickerSelectedOptions = useMemo<ReadonlyArray<ComboboxOption<string>>>(
    () => picked.map((p) => ({ value: p.userId, label: p.name })),
    [picked],
  );

  const commitEmail = () => {
    const raw = emailDraft.trim().replace(/,$/, '').trim();
    if (raw.length === 0) {
      setEmailError(null);
      return;
    }
    if (!EMAIL_RE.test(raw)) {
      setEmailError('That doesn’t look like a valid email address.');
      return;
    }
    if (emails.includes(raw)) {
      setEmailDraft('');
      setEmailError(null);
      return;
    }
    setEmails((prev) => [...prev, raw]);
    setEmailDraft('');
    setEmailError(null);
    void lookupEmailMatch(raw);
  };

  const removeEmail = (e: string) => {
    setEmails((prev) => prev.filter((x) => x !== e));
    setEmailMatches((prev) => {
      const { [e.toLowerCase()]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const lookupEmailMatch = async (rawEmail: string) => {
    const key = rawEmail.toLowerCase();
    setEmailMatches((prev) => ({ ...prev, [key]: 'pending' }));
    try {
      const status = await fetchInviteEmailStatus({
        kind,
        groupId,
        email: rawEmail,
      });
      setEmailMatches((prev) => ({ ...prev, [key]: projectStatus(status) }));
    } catch {
      setEmailMatches((prev) => ({ ...prev, [key]: { kind: 'error' } }));
    }
  };

  const onEmailKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      commitEmail();
    } else if (ev.key === 'Backspace' && emailDraft.length === 0 && emails.length > 0) {
      ev.preventDefault();
      const last = emails[emails.length - 1];
      if (last !== undefined) removeEmail(last);
    }
  };

  // Non-blocking emails (the ones the Send button will actually submit).
  // Picked members from the typeahead are excluded from this set because
  // they already passed the server's invitability filter — the typeahead
  // hides already-members and recipients with live pending invites.
  const sendableEmails = useMemo(
    () =>
      emails.filter(
        (e) => !isBlockingState(emailMatches[e.toLowerCase()], roleOnAccept),
      ),
    [emails, emailMatches, roleOnAccept],
  );
  const hasBlocking = emails.length !== sendableEmails.length;

  const sendCount =
    picked.length +
    new Set(sendableEmails.map((e) => e.toLowerCase())).size;

  const onSend = () => {
    setError(null);
    const pickedEmails = picked.map((p) => p.email);
    const allEmails = Array.from(
      new Set([...pickedEmails, ...sendableEmails].map((e) => e.toLowerCase())),
    );
    if (allEmails.length === 0) {
      setError('Add at least one email or pick a member.');
      return;
    }
    sendInvitesMutation.mutate(
      {
        kind,
        groupId,
        emails: allEmails,
        roleOnAccept,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setStep('result');
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Failed to send invites.'),
      },
    );
  };

  const onSendMore = () => {
    setStep('compose');
    setResult(null);
    setPicked([]);
    setEmails([]);
    setEmailDraft('');
    setEmailError(null);
    setEmailMatches({});
    setRoleOnAccept('member');
    setError(null);
  };

  const requestClose = () => {
    if (step === 'result') {
      onOpenChange(false);
      return;
    }
    const dirty = picked.length > 0 || emails.length > 0;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const confirmDiscard = () => {
    onOpenChange(false);
  };

  const kindNoun = kindLabel(kind);

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
      size="lg"
      accessibleTitle={`Invite to ${groupName}`}
    >
      <DialogSection bleed>
        <Row
          justify="space-between"
          align="center"
          gap="lg"
          py="lg" px="lg"
        >
          <Heading level={3}>
            {step === 'compose' ? `Invite to ${groupName}` : `Invites sent`}
          </Heading>
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label="Close"
            onClick={requestClose}
          />
        </Row>
      </DialogSection>

      {step === 'compose' ? (
        <DialogSection scrollable divider>
          <Stack gap="lg">
            <Field
              label="Invite member"
              description="Search by name — they’ll get an in-app notification."
            >
              <MultiCombobox<string>
                ariaLabel="Search Lean In members"
                placeholder="Search Lean In members…"
                options={pickerOptions}
                value={picked.map((p) => p.userId)}
                selectedItems={pickerSelectedOptions}
                onChange={(nextIds) => {
                  const idToHit = new Map(visibleHits.map((h) => [h.userId, h] as const));
                  const idToPicked = new Map(picked.map((p) => [p.userId, p] as const));
                  const nextPicked: PickedMember[] = [];
                  for (const id of nextIds) {
                    const existing = idToPicked.get(id);
                    if (existing !== undefined) {
                      nextPicked.push(existing);
                      continue;
                    }
                    const hit = idToHit.get(id);
                    if (hit !== undefined) {
                      nextPicked.push({
                        userId: hit.userId,
                        email: hit.email,
                        name: fullName(hit.firstName, hit.lastName),
                        photoUrl: hit.photoUrl,
                        jobTitle: hit.jobTitle,
                        company: hit.company,
                      });
                    }
                  }
                  setPicked(nextPicked);
                  setSearch('');
                  setHits([]);
                }}
                onQueryChange={setSearch}
                noInternalFilter
                emptyLabel={search.trim().length === 0 ? 'Type to search members…' : 'No matches'}
                renderOption={(opt) => {
                  const hit = visibleHits.find((h) => h.userId === opt.value);
                  if (hit === undefined) return null;
                  const name = fullName(hit.firstName, hit.lastName);
                  const sub = memberSublabel(hit.jobTitle, hit.company);
                  return (
                    <PersonRow
                      {...(hit.photoUrl ? { photo: hit.photoUrl } : {})}
                      name={name}
                      avatarSize={32}
                      {...(sub.length > 0 ? { sublabel: sub } : {})}
                    />
                  );
                }}
                renderChip={(opt, { onRemove }) => {
                  const member = picked.find((p) => p.userId === opt.value);
                  if (member === undefined) return null;
                  return <PickedChip member={member} onRemove={onRemove} />;
                }}
              />
            </Field>

            <Stack gap="xs">
              <Checkbox
                checked={roleOnAccept === 'leader'}
                onCheckedChange={(v) => setRoleOnAccept(v === true ? 'leader' : 'member')}
                label="Invite as leader"
              />
              <Body size="sm" color="mute">
                {roleOnAccept === 'leader'
                  ? `Everyone in this batch joins as a co-leader of this ${kind}.`
                  : 'Invitees join as members by default.'}
              </Body>
            </Stack>

            <Field
              label="Invite by email"
              {...(emailError !== null ? { error: emailError } : {})}
            >
              <Stack gap="sm">
                {emails.length > 0 ? (
                  <Row gap="sm" wrap>
                    {emails.map((e) => (
                      <EmailChip
                        key={e}
                        email={e}
                        state={emailMatches[e.toLowerCase()]}
                        kindNoun={kindNoun}
                        roleOnAccept={roleOnAccept}
                        onRemove={() => removeEmail(e)}
                      />
                    ))}
                  </Row>
                ) : null}
                <Input
                  type="email"
                  value={emailDraft}
                  placeholder="name@company.com (Enter or comma to add)"
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={onEmailKeyDown}
                  onBlur={commitEmail}
                  invalid={emailError !== null}
                />
                {(() => {
                  const summary = chipsSummary(
                    emails,
                    emailMatches,
                    kindNoun,
                    roleOnAccept,
                  );
                  if (summary.skippedTotal === 0) return null;
                  const titleSuffix =
                    summary.skippedTotal === 1 ? 'will be skipped' : 'will be skipped';
                  return (
                    <Notice
                      intent="warning"
                      placement="inline"
                      title={`${summary.skippedTotal} ${titleSuffix}`}
                      dismissible={false}
                      announceOn="transition"
                    >
                      {summary.skippedReasons.join(' · ')}. {summary.ready} ready to invite.
                    </Notice>
                  );
                })()}
              </Stack>
            </Field>

            <Field
              label="Add a personal note (optional)"
              description="Included in the email invite. Click Save as default to reuse next time."
              {...(noteSaveError !== null ? { error: noteSaveError } : {})}
            >
              <Stack gap="sm">
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={`A line about the ${kindNoun} and why you’re inviting them.`}
                />
                <Row gap="sm" align="center" justify="space-between" wrap>
                  <Body size="sm" color="mute">
                    {noteDirty
                      ? savedNote.length > 0
                        ? 'Unsaved changes — your default is still the previous note.'
                        : 'This note is only for this batch unless you save it as default.'
                      : savedNote.length > 0
                        ? 'Saved as default for this ' + kindNoun.toLowerCase() + '.'
                        : null}
                  </Body>
                  <Button
                    variant={noteDirty ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={onSaveNoteAsDefault}
                    disabled={!noteDirty}
                    loading={setInviteNote.isPending}
                    {...(!noteDirty && (noteJustSaved || savedNote.length > 0)
                      ? { icon: I.check }
                      : {})}
                  >
                    {noteJustSaved
                      ? 'Saved!'
                      : !noteDirty && savedNote.length > 0
                        ? 'Saved'
                        : 'Save as default'}
                  </Button>
                </Row>
              </Stack>
            </Field>

            {error !== null ? (
              <span role="alert" aria-live="polite">
                <Body size="sm" color="destructive">
                  {error}
                </Body>
              </span>
            ) : null}
          </Stack>
        </DialogSection>
      ) : (
        <DialogSection scrollable divider>
          <ResultPanel result={result} kindNoun={kindNoun} />
        </DialogSection>
      )}

      {step === 'compose' ? (
        <DialogFooter justify="space-between">
          <Row gap="sm" align="center" style={{ minWidth: 0 }}>
            <Icon size={14} color="var(--muted-foreground)">
              {I.shield}
            </Icon>
            <Body size="sm" color="mute">
              Invitees see your name, the {kindNoun} name, and your note —
              nothing else from your profile.
            </Body>
          </Row>
          <Row gap="md" align="center" style={{ flexShrink: 0 }}>
            <Button
              variant="ghost"
              onClick={requestClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              iconRight={I.send}
              onClick={onSend}
              disabled={sendCount === 0 || hasBlocking}
              loading={isSending}
            >
              {`Send ${sendCount} ${sendCount === 1 ? 'invite' : 'invites'}`}
            </Button>
          </Row>
        </DialogFooter>
      ) : (
        <DialogFooter justify="space-between">
          <Button variant="secondary" onClick={onSendMore}>
            Send more
          </Button>
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      )}
    </Dialog>

      <AlertDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard unsaved invites?"
        description="Your picked members and email addresses will be lost. Your saved default note stays."
        actionLabel="Discard"
        cancelLabel="Keep editing"
        onAction={confirmDiscard}
      />
    </>
  );
}

function PickedChip({
  member,
  onRemove,
}: {
  member: PickedMember;
  onRemove: () => void;
}) {
  const sub = memberSublabel(member.jobTitle, member.company);
  return (
    <Row
      gap="sm"
      align="center"
      style={{
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- pill-shaped chip with asymmetric padding (start vs end); no Box prop for asymmetric inline padding
        padding: 'var(--space-xs) var(--space-sm) var(--space-xs) var(--space-xs)',
        border: `1px solid ${TONE.neutral.bg}`,
        background: 'var(--muted)',
        borderRadius: 'var(--radius-full)',
        maxWidth: '100%',
      }}
    >
      <Avatar
        {...(member.photoUrl ? { src: member.photoUrl } : {})}
        name={member.name}
        size={22}
      />
      <span
        style={{
          font: 'var(--type-label)',
          color: 'var(--foreground)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 220,
        }}
      >
        {member.name}
        {sub.length > 0 ? (
          <span style={{ color: 'var(--muted-foreground)' }}>
            {' '}
            · {sub}
          </span>
        ) : null}
      </span>
      <Button
        variant="ghost"
        size="sm"
        icon={I.x}
        aria-label={`Remove ${member.name}`}
        onClick={onRemove}
      />
    </Row>
  );
}

// Per-chip visual + tooltip projected from the server's status probe.
// The chip itself encodes everything the leader needs: avatar/glyph slot,
// label, hover tooltip with the long-form reason. Industry-standard for
// bulk-invite modals (Slack, Linear, Notion, Stripe) — see design notes
// in PR. Pre-redesign we had a stacked `<email> — <long sentence>` row
// per chip plus a persistent helper paragraph; that scaled to ~3 chips
// before the modal turned into a wall of text.
type EmailChipVisual = {
  readonly label: string;
  readonly leading: 'avatar' | { readonly glyph: ReactNode };
  readonly statusGlyph: ReactNode | null;
  readonly tone: 'matched' | 'neutral' | 'muted' | 'destructive' | 'pending';
  readonly tooltip: string;
};

function chipVisual(
  email: string,
  state: EmailMatchState | undefined,
  kindNoun: 'Circle' | 'Network',
  roleOnAccept: 'member' | 'leader',
): EmailChipVisual {
  if (state === undefined || state === 'pending') {
    return {
      label: email,
      leading: { glyph: I.search },
      statusGlyph: null,
      tone: 'pending',
      tooltip: 'Checking…',
    };
  }
  const lowerNoun = kindNoun.toLowerCase();
  switch (state.kind) {
    case 'on_platform_invitable':
      return {
        label: state.name,
        leading: 'avatar',
        statusGlyph: null,
        tone: 'matched',
        tooltip:
          state.sublabel.length > 0
            ? `${email} · ${state.sublabel}`
            : email,
      };
    case 'off_platform':
      return {
        label: email,
        leading: { glyph: I.mail },
        statusGlyph: null,
        tone: 'neutral',
        tooltip: `Not on Lean In yet — the invite shows up when they sign in.`,
      };
    case 'already_member': {
      // Promote case: existing member, leader toggled "Invite as leader".
      // Chip stays full-tone (not skipped), shows an up-chevron, and the
      // tooltip names the role bump. Server applies the promote in the
      // same tx as the bulk-create call (no separate API hit).
      if (state.role === 'member' && roleOnAccept === 'leader') {
        return {
          label: state.name,
          leading: 'avatar',
          statusGlyph: <ArrowUpGlyph size={12} strokeWidth={2} />,
          tone: 'matched',
          tooltip: `Already a member — will be promoted to leader of this ${lowerNoun}.`,
        };
      }
      return {
        label: state.name,
        leading: 'avatar',
        statusGlyph: I.check,
        tone: 'muted',
        tooltip: `Already a ${state.role} of this ${lowerNoun} — will be skipped.`,
      };
    }
    case 'pending_invite':
      return {
        label: email,
        leading: { glyph: <ClockGlyph size={14} strokeWidth={1.75} /> },
        statusGlyph: <ClockGlyph size={14} strokeWidth={1.75} />,
        tone: 'muted',
        tooltip:
          state.inviterName.length > 0
            ? `Invited ${relativeDays(state.sentAtIso)} by ${state.inviterName} — won't send another.`
            : `Already invited ${relativeDays(state.sentAtIso)} — won't send another.`,
      };
    case 'invalid_email':
      return {
        label: email,
        leading: { glyph: I.alert },
        statusGlyph: I.alert,
        tone: 'destructive',
        tooltip: 'Not a valid email address.',
      };
    case 'error':
      return {
        label: email,
        leading: { glyph: I.alert },
        statusGlyph: null,
        tone: 'neutral',
        tooltip: "Couldn't check right now — we'll retry on send.",
      };
  }
}

function EmailChip({
  email,
  state,
  kindNoun,
  roleOnAccept,
  onRemove,
}: {
  email: string;
  state: EmailMatchState | undefined;
  kindNoun: 'Circle' | 'Network';
  roleOnAccept: 'member' | 'leader';
  onRemove: () => void;
}) {
  const v = chipVisual(email, state, kindNoun, roleOnAccept);
  const photoUrl =
    state !== undefined && state !== 'pending' &&
    (state.kind === 'on_platform_invitable' || state.kind === 'already_member')
      ? state.photoUrl
      : null;
  const memberName =
    state !== undefined && state !== 'pending' &&
    (state.kind === 'on_platform_invitable' || state.kind === 'already_member')
      ? state.name
      : email;

  const palette = (() => {
    switch (v.tone) {
      case 'matched':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--foreground)',
          opacity: 1,
        };
      case 'neutral':
      case 'pending':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--foreground)',
          opacity: 1,
        };
      case 'muted':
        return {
          bg: 'var(--muted)',
          border: 'var(--border)',
          ink: 'var(--muted-foreground)',
          opacity: 0.7,
        };
      case 'destructive':
        return {
          bg: 'var(--muted)',
          border: 'var(--destructive)',
          ink: 'var(--destructive)',
          opacity: 1,
        };
    }
  })();

  return (
    <Tooltip content={v.tooltip}>
      <Row
        gap="sm"
        align="center"
        style={{
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- pill-shaped chip with asymmetric padding (start vs end); no Box prop for asymmetric inline padding
          padding: 'var(--space-xs) var(--space-sm) var(--space-xs) var(--space-xs)',
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          borderRadius: 'var(--radius-full)',
          maxWidth: '100%',
          opacity: palette.opacity,
        }}
      >
        {v.leading === 'avatar' ? (
          <Avatar
            {...(photoUrl ? { src: photoUrl } : {})}
            name={memberName}
            size={22}
          />
        ) : (
          <Icon size={14} color="var(--muted-foreground)">
            {v.leading.glyph}
          </Icon>
        )}
        <span
          style={{
            font: 'var(--type-label)',
            color: palette.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 220,
          }}
        >
          {v.label}
        </span>
        {v.statusGlyph !== null ? (
          <Icon
            size={12}
            color={
              v.tone === 'destructive'
                ? 'var(--destructive)'
                : 'var(--muted-foreground)'
            }
          >
            {v.statusGlyph}
          </Icon>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          icon={I.x}
          aria-label={`Remove ${email}`}
          onClick={onRemove}
        />
      </Row>
    </Tooltip>
  );
}

// One-line aggregate summary shown beneath the chip strip when at least one
// chip is in a blocking state. Industry-standard pattern — the chips
// themselves carry per-recipient state via visual + tooltip; this caption
// just totals what will actually go out vs what's being skipped. Send
// button's "Send N invites" count is the canonical ready-count.
function chipsSummary(
  emails: readonly string[],
  matches: Record<string, EmailMatchState>,
  kindNoun: 'Circle' | 'Network',
  roleOnAccept: 'member' | 'leader',
): {
  readonly ready: number;
  readonly skippedTotal: number;
  readonly skippedReasons: readonly string[];
} {
  let ready = 0;
  let alreadyMember = 0;
  let pendingInvite = 0;
  let invalid = 0;
  for (const e of emails) {
    const s = matches[e.toLowerCase()];
    if (s === undefined || s === 'pending') {
      ready += 1;
      continue;
    }
    switch (s.kind) {
      case 'on_platform_invitable':
      case 'off_platform':
      case 'error':
        ready += 1;
        break;
      case 'already_member':
        // Promote path is ready, not skipped — server bumps role in-tx.
        if (s.role === 'member' && roleOnAccept === 'leader') {
          ready += 1;
        } else {
          alreadyMember += 1;
        }
        break;
      case 'pending_invite':
        pendingInvite += 1;
        break;
      case 'invalid_email':
        invalid += 1;
        break;
    }
  }
  const skippedReasons: string[] = [];
  if (alreadyMember > 0) {
    skippedReasons.push(
      `${alreadyMember} already in this ${kindNoun.toLowerCase()}`,
    );
  }
  if (pendingInvite > 0) skippedReasons.push(`${pendingInvite} already invited`);
  if (invalid > 0) skippedReasons.push(`${invalid} invalid`);
  return { ready, skippedTotal: alreadyMember + pendingInvite + invalid, skippedReasons };
}

function ResultPanel({
  result,
  kindNoun,
}: {
  result: BulkInviteResult | null;
  kindNoun: 'Circle' | 'Network';
}) {
  if (result === null) {
    return (
      <Body size="sm" color="mute">
        No result to show.
      </Body>
    );
  }

  const sent = result.items.length;
  const added = result.added.length;
  const promoted = result.promoted.length;
  const alreadyMembers = result.skipped.filter((s) => s.reason === 'already_member');
  const duplicates = result.skipped.filter((s) => s.reason === 'duplicate');
  // The server only emits `duplicate` and `already_member` for skipped
  // recipients. Invalid emails are blocked client-side via the per-chip
  // probe — they never reach POST — so there's no "invalid" bucket to
  // surface here. Same for the `pending_invite` block: the chip-status
  // probe catches those before submit.

  return (
    <Stack gap="lg">
      <Row gap="sm" wrap>
        {sent > 0 ? (
          <Pill size="md" tone="emerald">
            {sent} {sent === 1 ? 'invite sent' : 'invites sent'}
          </Pill>
        ) : null}
        {added > 0 ? (
          <Pill size="md" tone="emerald">
            {added} {added === 1 ? 'added directly' : 'added directly'}
          </Pill>
        ) : null}
        {promoted > 0 ? (
          <Pill size="md" tone="emerald">
            {promoted} promoted to leader
          </Pill>
        ) : null}
        {alreadyMembers.length > 0 ? (
          <Pill size="md" tone="neutral">
            {alreadyMembers.length} already {alreadyMembers.length === 1 ? 'a member' : 'members'}
          </Pill>
        ) : null}
        {duplicates.length > 0 ? (
          <Pill size="md" tone="neutral">
            {duplicates.length} already had pending {duplicates.length === 1 ? 'invite' : 'invites'}
          </Pill>
        ) : null}
      </Row>

      {added > 0 ? (
        <ResultGroup
          title={`Added to the ${kindNoun.toLowerCase()} directly`}
          description="These recipients are already on Lean In, so they joined immediately — no email needed."
          tone="positive"
        >
          {result.added.map((a) => (
            <ResultRow
              key={a.email}
              primary={fullName(a.firstName, a.lastName)}
              secondary={a.email}
            />
          ))}
        </ResultGroup>
      ) : null}

      {promoted > 0 ? (
        <ResultGroup
          title="Promoted to leader"
          description="These recipients were already members; their role was elevated to leader. They got an in-app notification — no email."
          tone="positive"
        >
          {result.promoted.map((p) => (
            <ResultRow
              key={p.email}
              primary={fullName(p.firstName, p.lastName)}
              secondary={p.email}
            />
          ))}
        </ResultGroup>
      ) : null}

      {sent > 0 ? (
        <ResultGroup
          title="Invitations emailed"
          description="The email will land in their inbox shortly."
          tone="neutral"
        >
          {result.items.map((it) => (
            <ResultRow key={it.id} primary={it.invitedEmail} />
          ))}
        </ResultGroup>
      ) : null}

      {alreadyMembers.length > 0 ? (
        <ResultGroup
          title={`Already in this ${kindNoun.toLowerCase()}`}
          description="No invite was sent — these recipients are already members."
          tone="muted"
        >
          {alreadyMembers.map((s) => (
            <ResultRow key={s.email} primary={s.email} />
          ))}
        </ResultGroup>
      ) : null}

      {duplicates.length > 0 ? (
        <ResultGroup
          title="Already had a pending invite"
          description="A live invite already exists for these addresses, so we didn’t send another."
          tone="muted"
        >
          {duplicates.map((s) => (
            <ResultRow key={s.email} primary={s.email} />
          ))}
        </ResultGroup>
      ) : null}
    </Stack>
  );
}

function ResultGroup({
  title,
  description,
  tone,
  children,
}: {
  title: string;
  description: string;
  tone: 'positive' | 'neutral' | 'muted';
  children: React.ReactNode;
}) {
  const icon =
    tone === 'positive' ? I.check : tone === 'neutral' ? I.send : I.alert;
  const iconColor =
    tone === 'positive'
      ? 'var(--success)'
      : tone === 'neutral'
        ? 'var(--muted-foreground)'
        : 'var(--muted-foreground)';
  return (
    <Stack gap="sm">
      <Row gap="sm" align="center">
        <Icon size={14} color={iconColor}>{icon}</Icon>
        <Body size="md">
          <strong>{title}</strong>
        </Body>
      </Row>
      <Body size="sm" color="mute">{description}</Body>
      <Stack gap="xs">
        {children}
      </Stack>
    </Stack>
  );
}

function ResultRow({
  primary,
  secondary,
}: {
  primary: string;
  secondary?: string;
}) {
  // `<Body>` defaults to `<p>`, which carries the browser's default top/bottom
  // margin. Inside this horizontal `<Row>` that margin manifests as a blank
  // line between the name and the email — render inline as `<span>` so the
  // two sit on one line at the row's natural baseline.
  return (
    <Row gap="sm" align="center">
      <Body size="sm" as="span">{primary}</Body>
      {secondary !== undefined ? (
        <Body size="sm" color="mute" as="span">· {secondary}</Body>
      ) : null}
    </Row>
  );
}
